import { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import Breadcrumb from '../components/Breadcrumb'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Link } from 'react-router'

gsap.registerPlugin(ScrollTrigger)

const sections = [
  {
    id: 'introduction',
    title: 'INTRODUCTION',
    content: `These Terms of Service ("Terms," "Agreement") are an agreement between Technifire Software LLC ("Technifire," "We," "us") and the person or entity ("You," "Customer") and govern Your use of the technifire.io website ("Site"), as well as Technifire professional and enterprise services ("Services"). Your use of the Site and Services is conditioned on Your acceptance without modification of the terms, conditions, and notices contained herein, including but not limited to Technifire's Privacy Policy, as amended from time to time.

If You are accessing or using the Site or the Services on behalf of Your company, You certify that You have the authority to accept this Agreement on behalf of it. By signing up, accessing, or using the Site or the Services, You acknowledge Your acceptance of this Agreement and consent to be bound by its terms and conditions. Violations of these Terms may result in termination of Service and legal action against You.

Do not use the Site or the Services if You do not agree to the Terms or if Your jurisdiction will not honor them. Persons under the age of 13 are not authorized to use the Site.`
  },
  {
    id: 'our-services',
    title: 'OUR SERVICES',
    content: `Technifire provides full-service digital agency services including website development, SaaS platform development, e-commerce solutions, CRM systems, branding & design, landing pages, and AI automation & chatbot services. Our services are delivered by our in-house team of designers, developers, and strategists based in Miami, Florida.

Professional services include personalized consultations, implementation assistance, optimization strategies, and ongoing support tailored to Your specific needs. Please contact us at https://technifire.io/contacts with any questions or requests.`
  },
  {
    id: 'payment-terms',
    title: 'PAYMENT TERMS',
    content: `Payment
Services are quoted on a project basis or monthly subscription basis as outlined in your proposal or Statement of Work ("SOW"). Upon agreement, You will provide Technifire with valid payment information. You authorize Technifire to charge Your payment method for any unpaid charges related to the Services, including applicable taxes.

Subscription
If You wish to cancel a subscription-based service, You must provide written notice before the end of the current billing period. Failure to do so will result in automatic renewal for the next period.

Late Payment
Invoices are due within 14 days of receipt. Past due amounts may incur a late fee of 1.5% per month (or the maximum allowed by law). Technifire reserves the right to suspend Services for non-payment after a 7-day grace period. You are responsible for all collection costs, including reasonable attorney's fees.

Rate Changes
Technifire may change rates for subscription services with 30 days' written notice. New rates apply to the next billing cycle.`
  },
  {
    id: 'acceptable-use',
    title: 'ACCEPTABLE USE POLICY',
    content: `You agree to use the Services only for lawful purposes and in compliance with all applicable laws. You shall not:

- Use the Services to violate any local, state, national, or international law
- Infringe the intellectual property rights of others
- Transmit harmful, threatening, abusive, harassing, or defamatory content
- Interfere with or disrupt the Services, servers, or networks
- Attempt to gain unauthorized access to any systems or data
- Use the Services for spam, phishing, or fraudulent activities
- Reverse engineer, decompile, or attempt to derive source code from the Services

Technifire reserves the right to immediately terminate or suspend Your access if We determine, in Our sole discretion, that You have violated this Acceptable Use Policy.`
  },
  {
    id: 'indemnification',
    title: 'INDEMNIFICATION',
    content: `You agree to indemnify, defend, and hold harmless Technifire, its officers, directors, employees, agents, and third parties from any losses, costs, liabilities, and expenses (including reasonable attorney's fees) relating to or arising out of Your use of the Site or Services, Your violation of these Terms, Your violation of any third-party rights, or Your violation of applicable laws. Technifire reserves the right to assume the exclusive defense of any matter subject to indemnification by You.`
  },
  {
    id: 'warranty-disclaimer',
    title: 'WARRANTY DISCLAIMER',
    content: `THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. TECHNIFIRE DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, OR THAT DEFECTS WILL BE CORRECTED. TECHNIFIRE IS NOT RESPONSIBLE FOR DATA LOSS OR DAMAGE RESULTING FROM USE OF THE SERVICES. SOME JURISDICTIONS DO NOT ALLOW CERTAIN WARRANTY DISCLAIMERS, SO SOME OF THE ABOVE MAY NOT APPLY TO YOU.`
  },
  {
    id: 'limitation-of-liability',
    title: 'LIMITATION OF LIABILITY',
    content: `TECHNIFIRE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF PROFITS, GOODWILL, USE, OR DATA, EVEN IF ADVISED OF THE POSSIBILITY. OUR AGGREGATE LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID FOR THE SERVICE GIVING RISE TO THE CLAIM IN THE 12 MONTHS PRIOR, OR ONE HUNDRED DOLLARS ($100). THESE LIMITATIONS APPLY TO THE MAXIMUM EXTENT PERMITTED BY LAW.`
  },
  {
    id: 'intellectual-property',
    title: 'INTELLECTUAL PROPERTY RIGHTS',
    content: `All rights, title, and interest in the Services, Site, and content are the exclusive property of Technifire, except User Content. Technifire grants You a limited, non-exclusive, non-transferable, revocable license to access and use the Services for Your internal business purposes.

User-Generated Content
You retain ownership of content You submit ("User Content"). By submitting, You grant Technifire a worldwide, non-exclusive, royalty-free license to use, reproduce, and display User Content in connection with the Services.

Feedback
If You provide feedback or suggestions, You grant Technifire a perpetual, irrevocable, worldwide, royalty-free license to use and incorporate such Feedback without compensation.

Trademarks
All trademarks and trade names on the Site are property of their respective owners. You may not use them without prior written consent.`
  },
  {
    id: 'modifications',
    title: 'MODIFICATIONS TO TERMS',
    content: `Technifire reserves the right to modify these Terms at any time. Updated versions will be posted on the Site with a revised "Last Updated" date. Your continued use after changes constitutes acceptance. We encourage You to review these Terms periodically.`
  },
  {
    id: 'dispute-resolution',
    title: 'DISPUTE RESOLUTION',
    content: `Pre-Trial Resolution
For disputes, You must first send written notice to Technifire at the address below. We agree to negotiate in good faith for 60 days before either party may pursue legal action.

Class Action Waiver
Any proceedings will be conducted on an individual basis only, not as a class or representative action.

Governing Law
This Agreement is governed by the laws of the State of Florida, without regard to conflict of law principles.

Exclusive Jurisdiction
State and federal courts in Miami-Dade County, Florida have exclusive jurisdiction over any disputes. Both parties irrevocably submit to the personal jurisdiction of these courts.`
  },
  {
    id: 'severability',
    title: 'SEVERABILITY',
    content: `If any provision is found unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that the remaining Terms remain in full force and effect.`
  },
  {
    id: 'assignment',
    title: 'ASSIGNMENT',
    content: `You may not assign this Agreement without Our prior written consent. Technifire may assign or transfer this Agreement without notice. Upon assignment by Technifire, We shall be released from all liability.`
  },
  {
    id: 'force-majeure',
    title: 'FORCE MAJEURE',
    content: `Neither party is liable for delays or failures due to causes beyond reasonable control, including acts of God, war, terrorism, civil disorder, labor disputes, pandemics, network failures, or government actions. Performance times shall be extended by the duration of such events.`
  },
  {
    id: 'enforcement-waiver',
    title: 'ENFORCEMENT AND WAIVER',
    content: `Technifire may monitor, investigate, and enforce these Terms. Failure to enforce any provision in one instance does not constitute a waiver of that or any other provision. Technifire may report suspected criminal activity to law enforcement.`
  },
  {
    id: 'contact-us',
    title: 'CONTACT US',
    content: `If you have questions about these Terms of Service, please contact us:

Technifire  LLC
8301 State Line Rd Ste 220 # 3747, Kansas City, MO 64114-2025
Email: info@technifire.io
Phone: +1 (425) 541-4001
Website: https://technifire.io

Last Updated: July 16, 2026`
  }
]

export default function TermsOfService() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.policy-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>Terms of Service — Technifire Software</title>
        <meta name="description" content="Technifire Software Terms of Service — terms and conditions governing the use of our website, SaaS platforms, and digital agency services." />
        <link rel="canonical" href="https://technifire.io/terms-of-service" />
        <meta property="og:title" content="Terms of Service — Technifire Software" />
        <meta property="og:description" content="Terms and conditions governing the use of Technifire Software services." />
        <meta property="og:url" content="https://technifire.io/terms-of-service" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Breadcrumb current="TERMS OF SERVICE" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-12">
        <div className="max-w-4xl policy-animate">
          <span className="text-mono text-[#9A9A9A] mb-3 block">TERMS OF SERVICE</span>
          <h1 className="text-h2 uppercase text-white mb-6">
            OUR AGREEMENT<br />WITH YOU
          </h1>
          <p className="text-lg text-[#9A9A9A] leading-relaxed">
            These Terms of Service govern your use of Technifire services. Please read them carefully before using our platform.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm text-[#9A9A9A]">
            <span>Last Updated: July 16, 2026</span>
            <span className="text-white/30 mx-2">|</span>
            <span>Version 1.0</span>
            <span className="text-white/30 mx-2">|</span>
            <a href="mailto:info@technifire.io" className="text-white hover:text-[#9A9A9A] transition-colors">Contact Legal Team</a>
          </div>
        </div>
      </div>

      {/* Policy Sections */}
      <div className="px-6 lg:px-10">
        <div className="max-w-4xl space-y-12">
          {sections.map((section, index) => (
            <section key={section.id} id={section.id} className="policy-animate border-y border-white/10 py-10 first:pt-0 last:pb-0">
              <span className="text-mono text-[#9A9A9A] mb-4 block">
                {String(index + 1).padStart(2, '0')} / {sections.length}
              </span>
              <h2 className="text-h3 text-white mb-6">{section.title}</h2>
              <div className="prose prose-invert max-w-none text-[#9A9A9A] leading-relaxed">
                {section.content.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 lg:px-10 py-16 policy-animate">
        <div className="bg-gradient-to-r from-[#111] to-[#0a0a0a] rounded-2xl p-8 lg:p-12 border border-white/5 text-center max-w-2xl mx-auto">
          <h2 className="text-h3 text-white mb-4">Have Questions About These Terms?</h2>
          <p className="text-[#9A9A9A] mb-8">Our legal team is here to help clarify any part of this agreement.</p>
          <Link to="/contactsor" className="btn-primary inline-flex justify-center">
            CONTACT LEGAL TEAM
          </Link>
        </div>
      </div>
    </div>
  )
}